import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react"
import axios from "axios"
import { toast } from "sonner"
import {
  createPermit,
  updatePermit,
  submitPermitWorkflow,
  validatePermitSubmitPolicy,
  type CreatePermitRequestPayload,
  type CreatePermitResponsePayload,
  type PermitSubmitPolicyMessage,
  type PermitSubmitPolicyValidationResult,
  type UpdatePermitRequestPayload,
} from "modules/permit"
import { simulateWorkflow, type WorkflowSimulationResult } from "modules/permit/api/workflowService"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import type { TemplateConfiguration, TemplateSection } from "shared/api/templateService"
import type { PermitFormState } from "../types"
import type { RequiredDocumentItem } from "./useRequiredDocuments"
import type { RefreshPermitDetailOptions } from "./usePermitDetailHydration"
import { requestNavCountsRefresh } from "modules/permit/utils/navCountRefresh"

interface UsePermitRequestActionsParams {
  tenantId: string
  templateConfig: TemplateConfiguration | null
  generalSection: TemplateSection | null
  permitData: PermitFormState
  requiredDocuments: RequiredDocumentItem[]
  uploadedFiles: UploadedFileItem[]
  buildPermitPayload: () => CreatePermitRequestPayload | null
  editingPermitId: string | null
  refreshPermitDetail: (options?: RefreshPermitDetailOptions) => void
  submitterUserId: string | null
  onPermitCreated?: (permitId: string, response: CreatePermitResponsePayload) => void
}

export interface UsePermitRequestActionsResult {
  handleSave: () => Promise<void>
  isSaving: boolean
  saveError: string | null
  saveMessage: string | null
  handleSimulateWorkflow: () => Promise<void>
  isSimulatingWorkflow: boolean
  simulationResult: WorkflowSimulationResult | null
  simulationError: string | null
  isSimulationDialogOpen: boolean
  setIsSimulationDialogOpen: Dispatch<SetStateAction<boolean>>
  handleSubmitWorkflow: () => Promise<boolean>
  isSubmittingWorkflow: boolean
  submitError: string | null
  submitMessage: string | null
  ensurePermitSaved: () => Promise<PersistPermitResult>
}

interface PersistPermitResult {
  succeeded: boolean
  permitId: string | null
  permitNumber: string | null
  createdNewPermit: boolean
}

export const usePermitRequestActions = ({
  tenantId,
  templateConfig,
  generalSection,
  permitData,
  requiredDocuments,
  uploadedFiles,
  buildPermitPayload,
  editingPermitId,
  refreshPermitDetail,
  submitterUserId,
  onPermitCreated,
}: UsePermitRequestActionsParams): UsePermitRequestActionsResult => {
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [isSimulatingWorkflow, setIsSimulatingWorkflow] = useState(false)
  const [simulationResult, setSimulationResult] = useState<WorkflowSimulationResult | null>(null)
  const [simulationError, setSimulationError] = useState<string | null>(null)
  const [isSimulationDialogOpen, setIsSimulationDialogOpen] = useState(false)
  const [isSubmittingWorkflow, setIsSubmittingWorkflow] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitMessage, setSubmitMessage] = useState<string | null>(null)

  const formatPolicyMessages = useCallback((messages: PermitSubmitPolicyMessage[]) => {
    return messages
      .map((item) => item.message?.trim())
      .filter((item): item is string => Boolean(item))
  }, [])

  const parsePolicyValidationPayload = useCallback((payload: unknown): PermitSubmitPolicyValidationResult | null => {
    if (!payload || typeof payload !== "object") {
      return null
    }

    const root = payload as { policyValidation?: unknown } & Partial<PermitSubmitPolicyValidationResult>
    const candidate =
      root.policyValidation && typeof root.policyValidation === "object"
        ? (root.policyValidation as Partial<PermitSubmitPolicyValidationResult>)
        : root

    return {
      canSubmit: candidate.canSubmit !== false,
      policyFound: candidate.policyFound === true,
      policyId: typeof candidate.policyId === "string" ? candidate.policyId : null,
      errors: Array.isArray(candidate.errors) ? candidate.errors : [],
      warnings: Array.isArray(candidate.warnings) ? candidate.warnings : [],
    }
  }, [])

  useEffect(() => {
    setSaveError(null)
    setSaveMessage(null)
    setSubmitError(null)
    setSubmitMessage(null)
  }, [editingPermitId])

  type SaveContext = "manual" | "submit"

  const persistPermit = useCallback(
    async (context: SaveContext): Promise<PersistPermitResult> => {
      const assignError = (message: string) => {
        if (context === "manual") {
          setSaveError(message)
          setSaveMessage(null)
        } else {
          setSubmitError(message)
          setSubmitMessage(null)
        }
      }

      const clearMessages = () => {
      if (context === "manual") {
        setSaveError(null)
        setSaveMessage(null)
      } else {
        setSubmitError(null)
        setSubmitMessage(null)
      }
      }

      if (!templateConfig) {
        assignError("\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25 Template \u0e01\u0e23\u0e38\u0e13\u0e32\u0e42\u0e2b\u0e25\u0e14\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e01\u0e48\u0e2d\u0e19")
        return { succeeded: false, permitId: editingPermitId, permitNumber: permitData.permitNo || null, createdNewPermit: false }
      }

      const isEditFlow = Boolean(editingPermitId)
      if (isEditFlow && !permitData.permitNo) {
        assignError("\u0e44\u0e21\u0e48\u0e1e\u0e1a\u0e40\u0e25\u0e02\u0e17\u0e35\u0e48 Permit \u0e01\u0e23\u0e38\u0e13\u0e32\u0e23\u0e35\u0e40\u0e1f\u0e23\u0e0a\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e41\u0e25\u0e30\u0e25\u0e2d\u0e07\u0e43\u0e2b\u0e21\u0e48\u0e2d\u0e35\u0e01\u0e04\u0e23\u0e31\u0e49\u0e07")
        return { succeeded: false, permitId: editingPermitId, permitNumber: permitData.permitNo || null, createdNewPermit: false }
      }

      if (!generalSection) {
        assignError("Template \u0e19\u0e35\u0e49\u0e44\u0e21\u0e48\u0e21\u0e35\u0e2b\u0e31\u0e27\u0e02\u0e49\u0e2d General")
        return { succeeded: false, permitId: editingPermitId, permitNumber: permitData.permitNo || null, createdNewPermit: false }
      }

      setIsSaving(true)
      clearMessages()

      try {
        const payload = buildPermitPayload()
        if (!payload) {
          assignError("\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e40\u0e15\u0e23\u0e35\u0e22\u0e21\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e43\u0e1a\u0e2d\u0e19\u0e38\u0e0d\u0e32\u0e15\u0e44\u0e14\u0e49")
          return { succeeded: false, permitId: editingPermitId, permitNumber: permitData.permitNo || null, createdNewPermit: false }
        }

        let response: CreatePermitResponsePayload
        let persistedPermitId = editingPermitId
        let persistedPermitNumber = permitData.permitNo?.trim() || null
        let createdNewPermit = false

        if (isEditFlow && editingPermitId) {
          const updatePayload: UpdatePermitRequestPayload = {
            ...payload,
            permitId: editingPermitId,
          }
          response = await updatePermit(editingPermitId, updatePayload)
          persistedPermitId = editingPermitId
          persistedPermitNumber = response?.permitNumber ?? persistedPermitNumber
        } else {
          response = await createPermit(payload)
          persistedPermitId = response?.permitId ?? null
          persistedPermitNumber = response?.permitNumber ?? persistedPermitNumber
          createdNewPermit = true
          if (response?.permitId) {
            onPermitCreated?.(response.permitId, response)
          }
        }

        if (context === "manual") {
          const successMessage = isEditFlow
            ? `Saved changes successfully (Permit ${response.permitNumber || "N/A"})`
            : `Saved successfully (Permit ${response.permitNumber || "N/A"})`
          setSaveMessage(successMessage)
        }

        return { succeeded: true, permitId: persistedPermitId, permitNumber: persistedPermitNumber, createdNewPermit }
      } catch (error) {
        console.error("Failed to save permit", error)
        const fallbackMessage = "\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08 \u0e01\u0e23\u0e38\u0e13\u0e32\u0e25\u0e2d\u0e07\u0e43\u0e2b\u0e21\u0e48\u0e2d\u0e35\u0e01\u0e04\u0e23\u0e31\u0e49\u0e07"
        let resolvedMessage = fallbackMessage
        if (axios.isAxiosError(error)) {
          resolvedMessage =
            (error.response?.data as any)?.message ??
            error.response?.statusText ??
            error.message ??
            fallbackMessage
        }
        assignError(resolvedMessage)
        return { succeeded: false, permitId: editingPermitId, permitNumber: permitData.permitNo || null, createdNewPermit: false }
      } finally {
        setIsSaving(false)
      }
    },
    [
      templateConfig,
      permitData.permitNo,
      generalSection,
      buildPermitPayload,
      editingPermitId,
      onPermitCreated,
    ],
  )

  const handleSave = useCallback(async () => {
    await persistPermit("manual")
  }, [persistPermit])

  const handleSimulateWorkflow = useCallback(async () => {
    if (!templateConfig) {
      setSimulationError("\u0e01\u0e23\u0e38\u0e13\u0e32\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e1b\u0e23\u0e30\u0e40\u0e20\u0e17\u0e43\u0e1a\u0e2d\u0e19\u0e38\u0e0d\u0e32\u0e15\u0e01\u0e48\u0e2d\u0e19\u0e08\u0e33\u0e25\u0e2d\u0e07 Workflow")
      setIsSimulationDialogOpen(true)
      return
    }

    if (!generalSection) {
      setSimulationError("Template \u0e19\u0e35\u0e49\u0e44\u0e21\u0e48\u0e21\u0e35\u0e2b\u0e31\u0e27\u0e02\u0e49\u0e2d General")
      setIsSimulationDialogOpen(true)
      return
    }

    const payload = buildPermitPayload()
    if (!payload) {
      setSimulationError("\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e40\u0e15\u0e23\u0e35\u0e22\u0e21\u0e02\u0e49\u0e2d\u0e21\u0e39\u0e25\u0e2a\u0e33\u0e2b\u0e23\u0e31\u0e1a\u0e08\u0e33\u0e25\u0e2d\u0e07 Workflow \u0e44\u0e14\u0e49")
      setIsSimulationDialogOpen(true)
      return
    }

    setIsSimulatingWorkflow(true)
    setSimulationResult(null)
    setSimulationError(null)

    try {
      const result = await simulateWorkflow(payload)
      setSimulationResult(result)
      setIsSimulationDialogOpen(true)
    } catch (error) {
      console.error("Failed to simulate workflow", error)
      let message = "\u0e08\u0e33\u0e25\u0e2d\u0e07 Workflow \u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08 \u0e01\u0e23\u0e38\u0e13\u0e32\u0e25\u0e2d\u0e07\u0e43\u0e2b\u0e21\u0e48"
      if (axios.isAxiosError(error)) {
        message =
          (error.response?.data as any)?.message ??
          error.response?.statusText ??
          error.message ??
          message
      }
      setSimulationError(message)
      setIsSimulationDialogOpen(true)
    } finally {
      setIsSimulatingWorkflow(false)
    }
  }, [templateConfig, generalSection, buildPermitPayload])
  const handleSubmitWorkflow = useCallback(async (): Promise<boolean> => {
    const saveResult = await persistPermit("submit")
    if (!saveResult.succeeded) {
      return false
    }

    const permitIdToSubmit = saveResult.permitId ?? editingPermitId
    if (!permitIdToSubmit) {
      setSubmitError("\u0e01\u0e23\u0e38\u0e13\u0e32\u0e1a\u0e31\u0e19\u0e17\u0e36\u0e01\u0e43\u0e1a\u0e07\u0e32\u0e19\u0e01\u0e48\u0e2d\u0e19\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34")
      setSubmitMessage(null)
      return false
    }

    setIsSubmittingWorkflow(true)
    setSubmitError(null)
    setSubmitMessage(null)

    try {
      const policyValidation = await validatePermitSubmitPolicy(
        permitIdToSubmit,
        tenantId,
        submitterUserId,
      )

      const policyErrors = formatPolicyMessages(policyValidation.errors)
      if (policyErrors.length > 0 || policyValidation.canSubmit === false) {
        const fallbackPolicyMessage = "ไม่ผ่านเงื่อนไข policy สำหรับการส่งอนุมัติ"
        setSubmitError(policyErrors.join(" | "))
        if (policyErrors.length === 0) {
          setSubmitError(fallbackPolicyMessage)
        }
        return false
      }

      const policyWarnings = formatPolicyMessages(policyValidation.warnings)
      if (policyWarnings.length > 0) {
        const shouldContinue = window.confirm(
          `\u0e1e\u0e1a\u0e04\u0e33\u0e40\u0e15\u0e37\u0e2d\u0e19\u0e01\u0e48\u0e2d\u0e19\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\n\n${policyWarnings.map((item) => `- ${item}`).join("\n")}\n\n\u0e15\u0e49\u0e2d\u0e07\u0e01\u0e32\u0e23\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e15\u0e48\u0e2d\u0e2b\u0e23\u0e37\u0e2d\u0e44\u0e21\u0e48?`,
        )

        if (!shouldContinue) {
          setSubmitMessage(policyWarnings.join(" | "))
          return false
        }
      }

      const response = await submitPermitWorkflow(
        permitIdToSubmit,
        tenantId,
        submitterUserId,
      )
      if (response.emailErrorMessage) {
        toast.error(`\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08 \u0e41\u0e15\u0e48\u0e2a\u0e48\u0e07\u0e2d\u0e35\u0e40\u0e21\u0e25\u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08: ${response.emailErrorMessage}`)
      }
      const responseWarnings = formatPolicyMessages(response.policyWarnings ?? [])
      const instanceFragment = response.permitWorkflowInstanceId
        ? response.permitWorkflowInstanceId.slice(0, 8).toUpperCase()
        : null
      setSubmitMessage(
        responseWarnings.length > 0
          ? `${
              instanceFragment
                ? `\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e40\u0e23\u0e35\u0e22\u0e1a\u0e23\u0e49\u0e2d\u0e22 (Workflow ${instanceFragment}...)`
                : "\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e40\u0e23\u0e35\u0e22\u0e1a\u0e23\u0e49\u0e2d\u0e22"
            } \u0e04\u0e33\u0e40\u0e15\u0e37\u0e2d\u0e19: ${responseWarnings.join(" | ")}`
          : instanceFragment
            ? `\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e40\u0e23\u0e35\u0e22\u0e1a\u0e23\u0e49\u0e2d\u0e22 (Workflow ${instanceFragment}...)`
            : "\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e40\u0e23\u0e35\u0e22\u0e1a\u0e23\u0e49\u0e2d\u0e22",
      )
      refreshPermitDetail({ hydrateForm: false })
      requestNavCountsRefresh()
      return true
    } catch (error) {
      console.error("Failed to submit permit workflow", error)
      let message = "\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e44\u0e21\u0e48\u0e2a\u0e33\u0e40\u0e23\u0e47\u0e08 \u0e01\u0e23\u0e38\u0e13\u0e32\u0e25\u0e2d\u0e07\u0e43\u0e2b\u0e21\u0e48"
      if (axios.isAxiosError(error)) {
        const policyValidation = parsePolicyValidationPayload(error.response?.data)
        const policyErrors = formatPolicyMessages(policyValidation?.errors ?? [])
        const policyWarnings = formatPolicyMessages(policyValidation?.warnings ?? [])

        if (policyErrors.length > 0 || policyWarnings.length > 0) {
          message = [...policyErrors, ...policyWarnings.map((item) => `\u0e04\u0e33\u0e40\u0e15\u0e37\u0e2d\u0e19: ${item}`)].join(" | ")
        } else if (policyValidation?.canSubmit === false) {
          message = "\u0e44\u0e21\u0e48\u0e1c\u0e48\u0e32\u0e19\u0e40\u0e07\u0e37\u0e48\u0e2d\u0e19\u0e44\u0e02 policy \u0e08\u0e36\u0e07\u0e22\u0e31\u0e07\u0e44\u0e21\u0e48\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e2a\u0e48\u0e07\u0e2d\u0e19\u0e38\u0e21\u0e31\u0e15\u0e34\u0e44\u0e14\u0e49"
        } else {
          message =
            (error.response?.data as any)?.message ??
            error.response?.statusText ??
            error.message ??
            message
        }
      }
      setSubmitError(message)
      return false
    } finally {
      setIsSubmittingWorkflow(false)
    }
  }, [
    persistPermit,
    editingPermitId,
    tenantId,
    submitterUserId,
    refreshPermitDetail,
    formatPolicyMessages,
    parsePolicyValidationPayload,
  ])

  const ensurePermitSaved = useCallback(async () => persistPermit("submit"), [persistPermit])

  return {
    handleSave,
    isSaving,
    saveError,
    saveMessage,
    handleSimulateWorkflow,
    isSimulatingWorkflow,
    simulationResult,
    simulationError,
    isSimulationDialogOpen,
    setIsSimulationDialogOpen,
    handleSubmitWorkflow,
    isSubmittingWorkflow,
    submitError,
    submitMessage,
    ensurePermitSaved,
  }
}

